import React, { useState, useMemo } from "react"
import DataTable from "aldoo-ra/DataTable"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import { PlusIcon, MagnifyingGlassIcon, TrashIcon } from "@heroicons/react/24/outline"

const ClassTable = ({
  classesData = [],
  selectedClasses = [],
  disabledClasses = new Set(),
  onClassSelection,
  onEditClass,
  onDeleteClass,
  onAddClass,
  readOnly = false
}) => {
  const [searchTerm, setSearchTerm] = useState("")

  // API method for client-side filtering
  const fetchFilteredClasses = async ({ search = {}, page = 1, itemsPerPage = 10 }) => {
    let filteredData = [...classesData]

    // Apply search filtering
    const searchValue = searchTerm || search.name || ""
    if (searchValue) {
      const lowerSearchTerm = searchValue.toLowerCase().trim()
      filteredData = filteredData.filter(item => {
        const itemName = (item.name || "").toLowerCase()
        const itemType = (item.type || "").toLowerCase()

        // Also search base class name (before any modifiers like :hover)
        const baseClassName = itemName.includes(':') ? itemName.split(':')[0] : itemName

        return itemName.includes(lowerSearchTerm) ||
               itemType.includes(lowerSearchTerm) ||
               baseClassName.includes(lowerSearchTerm)
      })
    }

    // Calculate pagination
    const startIndex = (page - 1) * itemsPerPage
    const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage)

    return {
      data: paginatedData,
      total: filteredData.length,
      totalItems: filteredData.length
    }
  }

  // Table column configuration
  const tableRenders = useMemo(() => [
    {
      id: "name",
      title: "Class Name",
      header: "Class Name",
      render: (value, item) => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm bg-admin_neutral_1 px-2 py-1 rounded">
            .{value}
          </span>
        </div>
      )
    },
    {
      id: "properties",
      title: "Actions",
      header: "Actions",
      render: (_, item) => (
        <div className="flex items-center gap-2">
          <Button
            variant="admin_secondary"
            onClick={(e) => {
              e.stopPropagation()
              onEditClass?.(item)
            }}
            className="text-xs px-2 py-1"
          >
            Edit Properties
          </Button>
          {!readOnly && (
            <Button
              variant="admin_secondary"
              onClick={(e) => {
                e.stopPropagation()
                onDeleteClass?.(item.name)
              }}
              className="text-xs px-2 py-1 text-admin_error hover:bg-admin_error hover:text-white"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      )
    }
  ], [onEditClass, onDeleteClass, readOnly])

  // Handle row selection
  const handleRowClick = (item) => {
    onClassSelection?.(item)
  }

  // Get row styling based on selection and disabled state
  const getRowStyle = (_, item) => {
    const isSelected = selectedClasses.some(cls => cls.name === item.name)
    const isDisabled = disabledClasses.has(item.name)

    if (isSelected && isDisabled) {
      // Selected but disabled - show muted primary
      return "bg-admin_primary bg-opacity-5 border-l-4 border-admin_primary opacity-50"
    } else if (isSelected) {
      // Selected and enabled - show normal primary
      return "bg-admin_primary bg-opacity-10 border-l-4 border-admin_primary"
    }
    return ""
  }

  return (
    <div className="flex-1 overflow-hidden">
      {/* Custom Search and Add Button Header */}
      <div className="px-6 py-4 border-b border-admin_bordered bg-admin_paper">
        <div className="flex items-center justify-between gap-4">
          {/* Search Input */}
          <div className="flex-1">
            <Input
              variant="admin_compact"
              placeholder="Search CSS classes..."
              leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
              showClearButton={true}
              className="w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
            />
          </div>

          {/* Add Class Button */}
          {!readOnly && (
            <Button
              variant="admin_primary"
              onClick={onAddClass}
              className="h-[40px] flex items-center justify-center bg-admin_primary hover:bg-admin_primary_dk text-white font-bold rounded-full py-2 px-4 transition duration-200 ease-in-out w-[160px] gap-2 flex-shrink-0"
            >
              <PlusIcon className="h-4 w-4" />
              Add Class
            </Button>
          )}
        </div>
      </div>

      {/* DataTable */}
      <DataTable
        apiMethod={fetchFilteredClasses}
        variant="admin"
        localUserPreferencesKey="htmlEditor.styleClasses"
        renders={tableRenders}
        title=""
        searchField="name"
        showSearch={false} // Hide DataTable's own search since we have custom one
        preferredHeight="calc(65vh - 80px)"
        itemsPerPage={100}
        refreshKey={`${classesData.length}-${searchTerm}-${JSON.stringify(classesData.map(cls => cls.name))}`}
        styleOverrides={{
          container: "flex flex-col border-0",
          header: "hidden", // Hide DataTable header completely
          tableWrapper: "overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper",
          footer: "flex-shrink-0 mt-auto",
          row: "cursor-pointer hover:bg-admin_neutral_1",
          getRowStyle,
          onRowClick: handleRowClick
        }}
        style={{ height: 'calc(65vh - 80px)' }}
      />
    </div>
  )
}

export default ClassTable